import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Tooltip
} from '@mui/material';

interface CellPropertiesModalProps {
  open: boolean;
  onClose: () => void;
  setCellBorderLeft: (style: string, width: string, color: string) => void;
  setCellBorderRight: (style: string, width: string, color: string) => void;
  setCellBorderTop: (style: string, width: string, color: string) => void;
  setCellBorderBottom: (style: string, width: string, color: string) => void;
  removeCellBorderLeft: () => void;
  removeCellBorderRight: () => void;
  removeCellBorderTop: () => void;
  removeCellBorderBottom: () => void;
  setCellBorderStyle: (style: string) => void;
  setCellBorderWidth: (width: string) => void;
  setCellBorderColor: (color: string) => void;
  removeCellBorders: () => void;
  setCellBackgroundColor: (color: string) => void;
}

const CellPropertiesModal: React.FC<CellPropertiesModalProps> = ({
  open,
  onClose,
  setCellBorderLeft,
  setCellBorderRight,
  setCellBorderTop,
  setCellBorderBottom,
  removeCellBorderLeft,
  removeCellBorderRight,
  removeCellBorderTop,
  removeCellBorderBottom,
  setCellBorderStyle,
  setCellBorderWidth,
  setCellBorderColor,
  removeCellBorders,
  setCellBackgroundColor
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 15, height: 15, border: '2px solid #1976d2', borderRadius: 0.5, backgroundColor: 'rgba(25, 118, 210, 0.1)' }} />
          <Typography variant="subtitle1" fontWeight={600} fontSize="0.9rem">Propriedades da Célula</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          {/* Bordas Individuais */}
          <Box>
            <Typography variant="caption" fontWeight={600} color="#1976d2" sx={{ mb: 0.5, display: 'block' }}>
              Bordas Individuais
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, mb: 0.5 }}>
              <Button variant="outlined" size="small" onClick={() => setCellBorderLeft('solid', '2px', '#1976d2')} sx={{ minHeight: 28, fontSize: '0.78rem' }}>←</Button>
              <Button variant="outlined" size="small" onClick={() => setCellBorderRight('solid', '2px', '#1976d2')} sx={{ minHeight: 28, fontSize: '0.78rem' }}>→</Button>
              <Button variant="outlined" size="small" onClick={() => setCellBorderTop('solid', '2px', '#1976d2')} sx={{ minHeight: 28, fontSize: '0.78rem' }}>↑</Button>
              <Button variant="outlined" size="small" onClick={() => setCellBorderBottom('solid', '2px', '#1976d2')} sx={{ minHeight: 28, fontSize: '0.78rem' }}>↓</Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
              <Button variant="text" size="small" color="error" onClick={removeCellBorderLeft} sx={{ minHeight: 22, fontSize: '10px' }}>❌</Button>
              <Button variant="text" size="small" color="error" onClick={removeCellBorderRight} sx={{ minHeight: 22, fontSize: '10px' }}>❌</Button>
              <Button variant="text" size="small" color="error" onClick={removeCellBorderTop} sx={{ minHeight: 22, fontSize: '10px' }}>❌</Button>
              <Button variant="text" size="small" color="error" onClick={removeCellBorderBottom} sx={{ minHeight: 22, fontSize: '10px' }}>❌</Button>
            </Box>
          </Box>

          <Divider />

          {/* Configurações Gerais */}
          <Box>
            <Typography variant="caption" fontWeight={600} color="#1976d2" sx={{ mb: 0.5, display: 'block' }}>
              Todas as Bordas
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
              <Box>
                <Typography variant="caption" fontWeight={500} sx={{ mb: 0.3, display: 'block' }}>Estilo:</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.3 }}>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderStyle('solid')} sx={{ fontSize: '0.7rem', py: 0.2 }}>Sólida</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderStyle('dashed')} sx={{ fontSize: '0.7rem', py: 0.2 }}>Tracejada</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderStyle('dotted')} sx={{ fontSize: '0.7rem', py: 0.2 }}>Pontilhada</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderStyle('double')} sx={{ fontSize: '0.7rem', py: 0.2 }}>Dupla</Button>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={500} sx={{ mb: 0.3, display: 'block' }}>Largura:</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.3 }}>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderWidth('1px')} sx={{ fontSize: '0.7rem', py: 0.2 }}>1px</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderWidth('2px')} sx={{ fontSize: '0.7rem', py: 0.2 }}>2px</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderWidth('3px')} sx={{ fontSize: '0.7rem', py: 0.2 }}>3px</Button>
                  <Button variant="outlined" size="small" onClick={() => setCellBorderWidth('4px')} sx={{ fontSize: '0.7rem', py: 0.2 }}>4px</Button>
                </Box>
              </Box>
            </Box>

            {/* Cores da borda */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" fontWeight={500}>Cor das Bordas:</Typography>
                <input
                  type="color"
                  onChange={(e) => setCellBorderColor(e.target.value)}
                  style={{ width: 28, height: 20, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 3 }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                {[
                  { color: '#000000', label: 'Preto' },
                  { color: '#666666', label: 'Cinza' },
                  { color: '#1976d2', label: 'Azul' },
                  { color: '#2e7d32', label: 'Verde' },
                  { color: '#d32f2f', label: 'Vermelho' },
                  { color: '#f57c00', label: 'Laranja' }
                ].map((item) => (
                  <Tooltip key={item.color} title={item.label}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCellBorderColor(item.color)}
                      sx={{ minWidth: 'auto', height: 22, backgroundColor: item.color, border: '1px solid #ccc', '&:hover': { backgroundColor: item.color, opacity: 0.8 } }}
                    />
                  </Tooltip>
                ))}
              </Box>

              <Button variant="contained" color="error" onClick={removeCellBorders} size="small" fullWidth sx={{ mt: 1, fontSize: '0.75rem', py: 0.3 }}>
                Remover Todas as Bordas
              </Button>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Cor de Fundo */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} color="#1976d2">Cor de Fundo</Typography>
                <input
                  type="color"
                  onChange={(e) => setCellBackgroundColor(e.target.value)}
                  style={{ width: 28, height: 20, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 3 }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                {[
                  { color: 'transparent', label: 'Remover' },
                  { color: '#e9e9e9', label: 'Cinza' },
                  { color: '#e3f2fd', label: 'Azul' },
                  { color: '#e8f5e9', label: 'Verde' },
                  { color: '#fff3e0', label: 'Laranja' },
                  { color: '#ffebee', label: 'Amarelo' },
                  { color: '#fce4ec', label: 'Vermelho' },
                  { color: '#f3e5f5', label: 'Rosa' }
                ].map((item) => (
                  <Tooltip key={item.color} title={item.label}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCellBackgroundColor(item.color)}
                      sx={{
                        minWidth: 'auto',
                        height: 22,
                        backgroundColor: item.color === 'transparent' ? '#fff' : item.color,
                        border: '1px solid #ccc',
                        backgroundImage: item.color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none',
                        backgroundSize: item.color === 'transparent' ? '8px 8px' : 'auto',
                        backgroundPosition: item.color === 'transparent' ? '0 0, 0 4px, 4px -4px, -4px 0px' : 'auto',
                        '&:hover': { backgroundColor: item.color === 'transparent' ? '#fff' : item.color, opacity: 0.8 }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 1.5, py: 0.8 }}>
        <Button onClick={onClose} variant="contained" size="small" sx={{ fontSize: '0.78rem' }}>
          Concluído
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CellPropertiesModal;